(() => {
  'use strict';

  const INSTANCE = '__stavMobileSafariHeaderPin';
  const prior = window[INSTANCE];
  if (prior && typeof prior.destroy === 'function') prior.destroy();

  const params = new URLSearchParams(window.location.search);
  const diagnosticsOn = params.get('safari_header_debug') === '1' || params.get('stavHeaderDiag') === '1';
  const forceEnabled = params.get('safari_header_force') === '1' || params.get('stavHeaderPin') === 'force';
  const disabled = params.get('stavHeaderPin') === '0';
  const mobile = window.matchMedia('(max-width: 1024.98px)');
  const vv = window.visualViewport;
  const ua = navigator.userAgent;
  const mobileSafari = navigator.vendor === 'Apple Computer, Inc.' &&
    /Safari/.test(ua) &&
    !/(CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo)/.test(ua) &&
    navigator.maxTouchPoints > 0;
  const correctionEligible = !disabled && Boolean(vv) && (forceEnabled || mobileSafari);

  let wrapper = null;
  let header = null;
  let destroyed = false;
  let paused = false;
  let active = false;
  let raf = 0;
  let causes = new Set();
  let appliedTop = 0;
  let pending = null;
  let correctionBlocked = false;
  let settleSteps = 0;
  let classObserver = null;
  let resizeObserver = null;
  let shield = null;
  let overlay = null;
  let output = null;
  let status = null;
  let lastOverlayPaint = 0;
  let originalTop = { value: '', priority: '' };
  let originalHeight = { value: '', priority: '' };

  const listeners = [];
  const samples = [];
  const EPSILON = 0.75;
  const RESPONSE_EPSILON = 1.25;
  const MAX_TOP = 96;
  const MAX_STEP = 32;
  const MAX_VERIFY_FRAMES = 3;
  const MAX_SETTLE_STEPS = 6;

  function findNodes() {
    wrapper = document.querySelector('body.collection .section-header');
    header = wrapper && wrapper.querySelector(':scope > header.header, :scope > header');
    return Boolean(wrapper && header);
  }

  function on(target, type, handler, options) {
    if (!target) return;
    target.addEventListener(type, handler, options);
    listeners.push(() => target.removeEventListener(type, handler, options));
  }

  function clamp(value, low, high) {
    return Math.max(low, Math.min(high, value));
  }

  function round(value) {
    return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
  }

  function restoreProperty(element, name, original) {
    if (!element) return;
    if (original.value) element.style.setProperty(name, original.value, original.priority);
    else element.style.removeProperty(name);
  }

  function setAppliedTop(value) {
    appliedTop = value;
    if (header) header.style.setProperty('--stav-pin-top', `${value}px`);
  }

  function updateSpacerHeight() {
    if (!header || !wrapper) return;
    const height = Math.ceil(header.getBoundingClientRect().height);
    const previous = parseFloat(wrapper.style.getPropertyValue('--stav-pin-height'));
    if (height > 0 && (!Number.isFinite(previous) || Math.abs(previous - height) >= 1)) {
      wrapper.style.setProperty('--stav-pin-height', `${height}px`);
    }
  }

  function createShield() {
    if (shield || !correctionEligible) return;
    shield = document.createElement('div');
    shield.setAttribute('data-stav-safari-header-shield', '');
    shield.setAttribute('data-active', 'false');
    shield.setAttribute('aria-hidden', 'true');
    document.body.appendChild(shield);
  }

  function setShieldActive(value) {
    if (!shield) return;
    if (value && header) {
      const coveredBottom = Math.min(200, Math.max(0, Math.ceil(header.getBoundingClientRect().bottom)));
      shield.style.setProperty('--stav-shield-height', `${coveredBottom}px`);
    }
    shield.setAttribute('data-active', value ? 'true' : 'false');
  }

  function shouldRun() {
    if (!correctionEligible || !mobile.matches || !vv || !wrapper || !header) return false;
    if (Math.abs(vv.scale - 1) > 0.02) return false;
    return wrapper.classList.contains('shopify-section-header-sticky');
  }

  function elementIdentity(element) {
    if (!element) return null;
    return {
      tag: element.tagName ? element.tagName.toLowerCase() : null,
      id: element.id || null,
      classes: element.classList ? Array.from(element.classList).slice(0, 8) : [],
    };
  }

  function record(event, frameCauses, extra = {}) {
    if (!diagnosticsOn || !wrapper || !header) return null;
    const now = performance.now();
    const h = header.getBoundingClientRect();
    const w = wrapper.getBoundingClientRect();
    const hc = getComputedStyle(header);
    const wc = getComputedStyle(wrapper);
    const shieldRect = shield ? shield.getBoundingClientRect() : null;
    const shieldStyle = shield ? getComputedStyle(shield) : null;
    const next = wrapper.nextElementSibling;
    const nextRect = next ? next.getBoundingClientRect() : null;
    let hit = null;
    if (h.top > 1) {
      const y = Math.max(0, Math.min(h.top / 2, window.innerHeight - 1));
      hit = elementIdentity(document.elementFromPoint(window.innerWidth / 2, y));
    }
    const root = getComputedStyle(document.documentElement);
    const body = getComputedStyle(document.body);
    const fixedYHits = {};
    [2, 20, 50, 80].forEach((y) => {
      if (y < window.innerHeight) fixedYHits[y] = elementIdentity(document.elementFromPoint(window.innerWidth / 2, y));
    });
    const sample = {
      sequence: samples.length ? samples[samples.length - 1].sequence + 1 : 1,
      elapsedMs: round(now),
      wallTimeISO: new Date().toISOString(),
      event,
      causes: frameCauses,
      active,
      classes: Array.from(wrapper.classList),
      correction: {
        appliedTop: round(appliedTop),
        pending: Boolean(pending),
        blocked: correctionBlocked,
        settleSteps,
        error: Object.prototype.hasOwnProperty.call(extra, 'error') ? round(extra.error) : null,
      },
      header: {
        top: round(h.top), right: round(h.right), bottom: round(h.bottom), left: round(h.left),
        width: round(h.width), height: round(h.height), position: hc.position,
        cssTop: hc.top, transform: hc.transform, opacity: hc.opacity,
        background: hc.backgroundColor, zIndex: hc.zIndex,
      },
      wrapper: {
        top: round(w.top), bottom: round(w.bottom), width: round(w.width), height: round(w.height),
        cssHeight: wc.height, cssMinHeight: wc.minHeight, position: wc.position,
        overflowX: wc.overflowX, overflowY: wc.overflowY,
      },
      shield: shieldRect ? {
        active: shield.getAttribute('data-active') === 'true',
        top: round(shieldRect.top), bottom: round(shieldRect.bottom),
        width: round(shieldRect.width), height: round(shieldRect.height),
        position: shieldStyle.position, display: shieldStyle.display,
        background: shieldStyle.backgroundColor, zIndex: shieldStyle.zIndex,
      } : null,
      nextContent: nextRect ? {
        identity: elementIdentity(next), top: round(nextRect.top), bottom: round(nextRect.bottom),
      } : null,
      viewport: {
        scrollX: round(window.scrollX), scrollY: round(window.scrollY),
        innerWidth: window.innerWidth, innerHeight: window.innerHeight,
        clientWidth: document.documentElement.clientWidth,
        clientHeight: document.documentElement.clientHeight,
        vvOffsetLeft: round(vv && vv.offsetLeft), vvOffsetTop: round(vv && vv.offsetTop),
        vvPageLeft: round(vv && vv.pageLeft), vvPageTop: round(vv && vv.pageTop),
        vvWidth: round(vv && vv.width), vvHeight: round(vv && vv.height),
        vvScale: round(vv && vv.scale),
      },
      document: {
        scrollingElement: document.scrollingElement && document.scrollingElement.tagName,
        htmlOverflowX: root.overflowX, htmlOverflowY: root.overflowY,
        bodyOverflowX: body.overflowX, bodyOverflowY: body.overflowY,
      },
      hitTestAboveHeader: hit,
      hitTestTopEdge: elementIdentity(document.elementFromPoint(window.innerWidth / 2, 1)),
      hitTestsAtFixedY: fixedYHits,
      orientation: screen.orientation ? screen.orientation.type : null,
      visibilityState: document.visibilityState,
    };
    samples.push(sample);
    if (samples.length > 300) samples.shift();
    renderOverlay(sample, now);
    return sample;
  }

  function renderOverlay(sample, now) {
    if (!output || now - lastOverlayPaint < 250) return;
    lastOverlayPaint = now;
    output.textContent = [
      `event: ${sample.event} #${sample.sequence}`,
      `mode: mobile Safari ${mobileSafari}; eligible ${correctionEligible}; mobile ${mobile.matches}`,
      `scrollY: ${sample.viewport.scrollY}`,
      `visualViewport: top ${sample.viewport.vvOffsetTop} / page ${sample.viewport.vvPageTop} / h ${sample.viewport.vvHeight} / scale ${sample.viewport.vvScale}`,
      `header: ${sample.header.top} → ${sample.header.bottom} (${sample.header.position}; css top ${sample.header.cssTop})`,
      `wrapper: ${sample.wrapper.top} → ${sample.wrapper.bottom} (${sample.wrapper.position}; h ${sample.wrapper.height})`,
      `shield: ${sample.shield ? `${sample.shield.top} → ${sample.shield.bottom}; active ${sample.shield.active}` : 'none'}`,
      `correction: ${sample.correction.appliedTop}; pending ${sample.correction.pending}; blocked ${sample.correction.blocked}`,
      `root overflow: ${sample.document.htmlOverflowX}/${sample.document.htmlOverflowY}`,
      `paint above header: ${sample.hitTestAboveHeader ? JSON.stringify(sample.hitTestAboveHeader) : 'none'}`,
      'When the gap appears: tap Mark, then Copy diagnostics and paste it in Telegram.',
    ].join('\n');
  }

  function frame() {
    raf = 0;
    if (destroyed || paused || !wrapper || !header) return;
    const frameCauses = Array.from(causes);
    causes.clear();
    const nextActive = shouldRun();
    setShieldActive(nextActive);

    if (nextActive !== active) {
      active = nextActive;
      pending = null;
      correctionBlocked = false;
      settleSteps = 0;
      if (!active) {
        setAppliedTop(0);
        record('deactivate', frameCauses);
        return;
      }
      updateSpacerHeight();
    }

    if (!active) {
      record('inactive', frameCauses);
      return;
    }

    const rect = header.getBoundingClientRect();
    const error = rect.top;

    if (pending) {
      const observedDelta = rect.top - pending.beforeRectTop;
      const expectedDelta = pending.nextTop - pending.beforeTop;
      pending.verifyFrames += 1;
      if (Math.abs(observedDelta - expectedDelta) <= RESPONSE_EPSILON) {
        pending = null;
      } else if (pending.verifyFrames < MAX_VERIFY_FRAMES) {
        record('await-response', frameCauses, { error });
        enqueue('verify');
        return;
      } else {
        setAppliedTop(pending.beforeTop);
        pending = null;
        correctionBlocked = true;
        record('correction-no-response', frameCauses, { error });
        return;
      }
    }

    record('measure', frameCauses, { error });
    if (correctionBlocked || Math.abs(error) <= EPSILON) return;
    if (settleSteps >= MAX_SETTLE_STEPS) {
      correctionBlocked = true;
      record('settle-limit', frameCauses, { error });
      return;
    }

    const delta = clamp(-error, -MAX_STEP, MAX_STEP);
    const nextTop = clamp(appliedTop + delta, -MAX_TOP, MAX_TOP);
    if (Math.abs(nextTop - appliedTop) < 0.5) return;

    pending = {
      beforeTop: appliedTop,
      nextTop,
      beforeRectTop: rect.top,
      verifyFrames: 0,
    };
    settleSteps += 1;
    setAppliedTop(nextTop);
    enqueue('verify');
  }

  function enqueue(cause) {
    if (destroyed) return;
    causes.add(cause);
    if (!raf) raf = requestAnimationFrame(frame);
  }

  function exportDiagnostics() {
    return {
      schema: 1,
      generatedAt: new Date().toISOString(),
      page: window.location.pathname,
      language: document.documentElement.lang,
      direction: document.documentElement.dir,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      maxTouchPoints: navigator.maxTouchPoints,
      mobileSafari,
      correctionEligible,
      samples: samples.slice(),
    };
  }

  function copyText(text, done) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
    } else {
      fallbackCopy(text, done);
    }
  }

  function fallbackCopy(text, done) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.readOnly = true;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try { document.execCommand('copy'); } catch (_) { /* no-op */ }
    textarea.remove();
    done();
  }

  function setStatus(message) {
    if (!status) return;
    status.textContent = message;
    setTimeout(() => {
      if (status && status.textContent === message) status.textContent = '';
    }, 1600);
  }

  function createOverlay() {
    if (!diagnosticsOn || overlay) return;
    overlay = document.createElement('aside');
    overlay.id = 'stav-header-diag';
    overlay.setAttribute('data-stav-safari-debug', '');
    overlay.setAttribute('role', 'region');
    overlay.setAttribute('aria-label', 'Safari header diagnostics');
    overlay.innerHTML = `
      <div class="stav-safari-debug__panel">
        <div class="stav-safari-debug__bar">
          <strong>Safari header diagnostic</strong>
          <button type="button" data-action="mark">Mark</button>
          <button type="button" data-action="pause" aria-pressed="false">Pause</button>
          <button type="button" data-action="copy">Copy diagnostics</button>
          <button type="button" data-action="clear">Clear</button>
        </div>
        <span class="stav-safari-debug__status" aria-live="polite"></span>
        <pre data-stav-safari-output></pre>
      </div>`;
    document.body.appendChild(overlay);
    output = overlay.querySelector('[data-stav-safari-output]');
    status = overlay.querySelector('.stav-safari-debug__status');
    overlay.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-action]');
      if (!button) return;
      const action = button.dataset.action;
      if (action === 'mark') {
        record('manual-mark', ['button']);
        setStatus('Marked');
      } else if (action === 'pause') {
        paused = !paused;
        button.setAttribute('aria-pressed', String(paused));
        button.textContent = paused ? 'Resume' : 'Pause';
        setStatus(paused ? 'Paused' : 'Resumed');
        if (!paused) enqueue('resume');
      } else if (action === 'copy') {
        copyText(JSON.stringify(exportDiagnostics(), null, 2), () => setStatus('Copied'));
      } else if (action === 'clear') {
        samples.length = 0;
        setStatus('Cleared');
        enqueue('clear');
      }
    });
  }

  function start() {
    if (!document.body || !document.body.classList.contains('collection')) return;
    if (!correctionEligible && !diagnosticsOn) return;
    if (!findNodes()) return;

    originalTop = {
      value: header.style.getPropertyValue('--stav-pin-top'),
      priority: header.style.getPropertyPriority('--stav-pin-top'),
    };
    originalHeight = {
      value: wrapper.style.getPropertyValue('--stav-pin-height'),
      priority: wrapper.style.getPropertyPriority('--stav-pin-height'),
    };

    createShield();
    createOverlay();
    classObserver = new MutationObserver(() => enqueue('class-mutation'));
    classObserver.observe(wrapper, { attributes: true, attributeFilter: ['class'] });

    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateSpacerHeight();
        enqueue('header-resize');
      });
      resizeObserver.observe(header);
    }

    on(window, 'scroll', () => enqueue('window-scroll'), { passive: true });
    on(window, 'resize', () => enqueue('window-resize'), { passive: true });
    on(window, 'orientationchange', () => enqueue('orientationchange'), { passive: true });
    on(vv, 'scroll', () => enqueue('vv-scroll'), { passive: true });
    on(vv, 'resize', () => enqueue('vv-resize'), { passive: true });
    if (vv && 'onscrollend' in vv) on(vv, 'scrollend', () => enqueue('vv-scrollend'), { passive: true });
    on(mobile, 'change', () => enqueue('media-change'));
    on(window, 'pagehide', () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    });
    on(window, 'pageshow', () => enqueue('pageshow'));

    updateSpacerHeight();
    enqueue('init');
  }

  function setTestOffset(value) {
    if (!diagnosticsOn) return false;
    if (!findNodes()) return false;
    const number = Number(value);
    if (Number.isFinite(number)) {
      header.style.setProperty('--stav-pin-test-offset', `${number}px`);
    } else {
      header.style.removeProperty('--stav-pin-test-offset');
    }
    pending = null;
    correctionBlocked = false;
    settleSteps = 0;
    setAppliedTop(0);
    enqueue('diagnostic-test-offset');
    return true;
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    if (raf) cancelAnimationFrame(raf);
    classObserver?.disconnect();
    resizeObserver?.disconnect();
    listeners.splice(0).forEach((remove) => remove());
    restoreProperty(header, '--stav-pin-top', originalTop);
    header?.style.removeProperty('--stav-pin-test-offset');
    restoreProperty(wrapper, '--stav-pin-height', originalHeight);
    shield?.remove();
    overlay?.remove();
    if (window[INSTANCE] && window[INSTANCE].destroy === destroy) delete window[INSTANCE];
  }

  window[INSTANCE] = {
    destroy,
    enqueue,
    mark: (label = 'manual') => record('manual-mark', [String(label)]),
    clear: () => { samples.length = 0; },
    export: exportDiagnostics,
    getLog: () => samples.slice(),
    setTestOffset,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
